import React, { useEffect, useEffectEvent, useMemo, useState } from "react";

import { Alert, Spinner, Text, useWizard } from "@performyard/py-components";

import EmployeeSelection from "../../../../../../components/EmployeeSelection";

import { useCreateUserSelection, useUserSelectionCount } from "../../../../../../api/hooks/defs/user-selection";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import type { CycleType } from "../../Config";
import { SUBJECTS_STEP_ID } from "./constants";

type SelectSubjectsStepProps = {
	stepTitle: string;
	cycleType: CycleType;
	groupId: string | null;
	userSelectionId: string | null;
	onSelectionCreated: (id: string) => void;
	showSelectionError: boolean;
};

const emptyExclusionList: string[] = [];

export const SelectSubjectsStep = ({
	stepTitle,
	cycleType,
	groupId,
	userSelectionId,
	onSelectionCreated,
	showSelectionError,
}: SelectSubjectsStepProps) => {
	const { setStepData, setNextDisabled } = useWizard();

	const isGroupCycle = cycleType === "group";

	const createSelection = useCreateUserSelection({
		onSuccess: (data) => onSelectionCreated(data.user_selection_id),
	});

	const initializeSelection = useEffectEvent(() => {
		if (!userSelectionId && !createSelection.isPending) {
			createSelection.mutate();
		}
	});

	useEffect(() => {
		initializeSelection();
	}, []);

	const { data: selectionCountData } = useUserSelectionCount(userSelectionId ?? "");
	const selectedCount = selectionCountData?.count ?? 0;

	useEffect(() => {
		if (userSelectionId) {
			setStepData(SUBJECTS_STEP_ID, { userSelectionId, selectedCount });
		}
	}, [userSelectionId, selectedCount, setStepData]);

	const isInitializing = !userSelectionId;
	const [isTableBusy, setIsTableBusy] = useState(false);

	useEffect(() => {
		setNextDisabled(isInitializing || isTableBusy);
		return () => setNextDisabled(false);
	}, [isInitializing, isTableBusy, setNextDisabled]);

	const { value: isGroupsEnabled } = useFeatureFlagQuery<boolean>("ff-is-groups-enabled", false);

	const customFieldsIdentifiers = useMemo(() => {
		const fields = ["Hire Date", "Manager"];
		if (isGroupsEnabled) fields.unshift("Group");
		return fields;
	}, [isGroupsEnabled]);

	const filters: Record<string, string | string[]> = useMemo(() => {
		const base: Record<string, string | string[]> = { canAssignReviewsTo: "true" };
		if (isGroupCycle && groupId) {
			base.groupID = groupId;
			base.membershipType = "member";
		}
		return base;
	}, [isGroupCycle, groupId]);

	if (isInitializing) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center">
			<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="text-center">
				{stepTitle}
			</Text>
			<Text size="paragraph-2" color="neutral-9" dangerouslySetClassName="text-center">
				Add Subjects
			</Text>
			<div className="mt-8 flex w-full max-w-[1096px] flex-col gap-4 px-4">
				{showSelectionError && (
					<Alert type="error" message="At least one subject must be selected to continue." />
				)}
				<EmployeeSelection
					selectionObjectID={userSelectionId}
					customFieldsIdentifiers={customFieldsIdentifiers}
					exclusionList={emptyExclusionList}
					filters={filters}
					mutationFilters={{ canAssignReviewsTo: true }}
					paginationAlignment="end"
					onlySelected={false}
					showGroupsButton={!isGroupCycle}
					onMutatingChange={setIsTableBusy}
					height="calc(100dvh - 350px)"
				/>
			</div>
		</div>
	);
};
